import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import authAxios from "../../services/Token";
import { apiImage } from "../../services/Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye, faFilter, faPen, faSave, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";

const columnas = [
  { key: "orden_interna", label: "Orden interna" },
  { key: "archivo", label: "Archivo" },
  { key: "orden_externa", label: "Orden externa" },
  { key: "fecha_inicial", label: "Fecha orden" },
  { key: "profesional", label: "Profesional" },
  { key: "cedula", label: "Cedula" },
  { key: "paciente", label: "Paciente" },
  /* { key: "factura", label: "Factura" },
  { key: "porcentaje", label: "Porcentaje" }, */
  { key: "valor_copago", label: "Valor copago" },
  { key: "valor_recibido", label: "Valor recibido" },
  { key: "estado_cuenta_nombre", label: "Estado cuenta" },
  { key: "fecha_recibo", label: "Fecha recibo" },
  { key: "comprobante", label: "Comprobante" },
  { key: "obs_interna", label: "Notas internas" },
];

const estadosCuenta = [
  { value: "", label: "Todos" },
  { value: "pendiente", label: "PENDIENTE" },
  { value: "1", label: "INICIADA" },
  { value: "2", label: "EN PROCESO" },
  { value: "3", label: "CANCELADO" },
  { value: "4", label: "DESISTE" },
];

const filtrosIniciales = {
  cedula: "",
  paciente: "",
  archivo: "",
  orden_interna: "",
  orden_externa: "",
  estado_cuenta: "",
  fecha_orden: "",
  fecha_recibo: "",
};

const formatoMoneda = (valor) => {
  const numero = Number(valor) || 0;
  return numero.toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  });
};

const formatoFecha = (valor) => {
  if (!valor) return "";
  return String(valor).substring(0, 10);
};

const claseEstado = (estado) => {
  const nombre = String(estado || "PENDIENTE").toUpperCase();
  if (nombre === "PENDIENTE") return "badge bg-danger";
  if (nombre === "CANCELADO") return "badge bg-success";
  if (nombre === "DESISTE") return "badge bg-warning text-dark";
  if (nombre === "EN PROCESO") return "badge bg-primary";
  if (nombre === "INICIADA") return "badge bg-secondary";
  return "badge bg-info text-dark";
};

const Reporte = () => {
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modalObservacion, setModalObservacion] = useState({ abierto: false, fila: null, valor: "" });
  const [modalEstado, setModalEstado] = useState({ abierto: false, fila: null, estado: "" });

  useEffect(() => {
    buscar();
  }, []);

  const cambiarFiltro = (event) => {
    const { name, value } = event.target;
    setFiltros((actual) => ({
      ...actual,
      [name]: value,
    }));
  };

  const buscar = async (event) => {
    if (event) event.preventDefault();

    setCargando(true);
    try {
      const respuesta = await authAxios.post("ordenes/reporte_copagos", filtros);
      setDatos(respuesta.data.datos || []);
    } catch (error) {
      Swal.fire("Reporte de copagos", "No fue posible consultar la informacion.", "error");
    } finally {
      setCargando(false);
    }
  };

  const limpiar = () => {
    setFiltros(filtrosIniciales);
    setDatos([]);
  };

  const abrirObservacion = (fila) => {
    setModalObservacion({
      abierto: true,
      fila,
      valor: fila.obs_interna || "",
    });
  };

  const guardarObservacion = async () => {
    try {
      await authAxios.post("ordenes/actualizar_observacion", {
        orden: modalObservacion.fila.id,
        obs_interna: modalObservacion.valor,
      });
      setModalObservacion({ abierto: false, fila: null, valor: "" });
      await buscar();
      Swal.fire("Reporte de copagos", "Observacion guardada correctamente.", "success");
    } catch (error) {
      Swal.fire("Reporte de copagos", "No fue posible guardar la observacion.", "error");
    }
  };

  const abrirEstado = (fila) => {
    setModalEstado({
      abierto: true,
      fila,
      estado: fila.estado_cuenta ? String(fila.estado_cuenta) : "",
    });
  };

  const guardarEstado = async () => {
    if (!modalEstado.estado) {
      Swal.fire("Reporte de copagos", "Seleccione un estado.", "info");
      return;
    }

    try {
      await authAxios.post("ordenes/actualizar_estado_recibo", {
        recibo: modalEstado.fila.recibo_id,
        estado_cuenta: modalEstado.estado,
      });
      setModalEstado({ abierto: false, fila: null, estado: "" });
      await buscar();
      Swal.fire("Reporte de copagos", "Estado del recibo actualizado.", "success");
    } catch (error) {
      Swal.fire("Reporte de copagos", "No fue posible actualizar el estado.", "error");
    }
  };

  const exportarExcel = () => {
    if (datos.length === 0) {
      Swal.fire("Reporte de copagos", "No hay datos para exportar.", "info");
      return;
    }

    const filas = datos.map((fila) => {
      const item = {};
      columnas.forEach((columna) => {
        if (columna.key === "valor_copago" || columna.key === "valor_recibido") {
          item[columna.label] = Number(fila[columna.key]) || 0;
          return;
        }

        if (columna.key === "fecha_inicial" || columna.key === "fecha_recibo") {
          item[columna.label] = formatoFecha(fila[columna.key]);
          return;
        }

        if (columna.key === "comprobante") {
          item[columna.label] = fila.comprobante ? `${apiImage}${fila.comprobante}` : "";
          return;
        }

        item[columna.label] = fila[columna.key] ?? "";
      });
      return item;
    });

    const hoja = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Reporte copagos");
    XLSX.writeFile(libro, `reporte_copagos_${new Date().toISOString().substring(0, 10)}.xlsx`);
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .reporte-copagos-table thead th {
            background: #003057 !important;
            color: #fff !important;
            border-color: #003057 !important;
            font-size: 12px;
            vertical-align: middle;
            white-space: nowrap;
          }

          .reporte-copagos-table tbody td {
            vertical-align: middle;
            font-size: 12px;
          }

          .reporte-copagos-table .notas-cell {
            min-width: 220px;
            max-width: 280px;
            white-space: normal;
          }

          .reporte-actions {
            min-width: 175px;
            white-space: nowrap;
          }

          .reporte-comprobante {
            min-width: 120px;
            white-space: nowrap;
          }
        `}
      </style>
      <div className="content">
        <div className="container-fluid">
          <br />
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <b>Reporte de copagos</b>
              <span className="badge bg-primary">{datos.length} registros</span>
            </div>
            <div className="card-body">
              <form onSubmit={buscar}>
                <div className="row g-2">
                  <div className="col-md-2">
                    <label htmlFor="cedula">Cedula</label>
                    <input className="form-control form-control-sm" id="cedula" name="cedula" value={filtros.cedula} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="paciente">Nombre paciente</label>
                    <input className="form-control form-control-sm" id="paciente" name="paciente" value={filtros.paciente} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="archivo">Numero archivo</label>
                    <input className="form-control form-control-sm" id="archivo" name="archivo" value={filtros.archivo} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="orden_interna">Orden interna</label>
                    <input className="form-control form-control-sm" id="orden_interna" name="orden_interna" value={filtros.orden_interna} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="orden_externa">Orden externa</label>
                    <input className="form-control form-control-sm" id="orden_externa" name="orden_externa" value={filtros.orden_externa} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="estado_cuenta">Estado cuenta</label>
                    <select className="form-select form-select-sm" id="estado_cuenta" name="estado_cuenta" value={filtros.estado_cuenta} onChange={cambiarFiltro}>
                      {estadosCuenta.map((estado) => (
                        <option key={estado.value} value={estado.value}>{estado.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="fecha_orden">Fecha orden</label>
                    <input type="date" className="form-control form-control-sm" id="fecha_orden" name="fecha_orden" value={filtros.fecha_orden} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-2">
                    <label htmlFor="fecha_recibo">Fecha recibo</label>
                    <input type="date" className="form-control form-control-sm" id="fecha_recibo" name="fecha_recibo" value={filtros.fecha_recibo} onChange={cambiarFiltro} />
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <div className="btn-group w-100">
                      <button type="submit" className="btn btn-primary btn-sm" disabled={cargando}>
                        <FontAwesomeIcon icon={faSearch} /> {cargando ? "Consultando..." : "Buscar"}
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={limpiar}>
                        <FontAwesomeIcon icon={faTimes} /> Limpiar
                      </button>
                      <button type="button" className="btn btn-success btn-sm" onClick={exportarExcel}>
                        <FontAwesomeIcon icon={faDownload} /> Excel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <FontAwesomeIcon icon={faFilter} /> Resultado
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-striped table-hover mb-0 reporte-copagos-table">
                  <thead>
                    <tr>
                      <th>Acciones</th>
                      {columnas.map((columna) => (
                        <th key={columna.key}>{columna.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {datos.length === 0 && (
                      <tr>
                        <td colSpan={columnas.length + 1} className="text-center py-4">
                          Sin registros para mostrar.
                        </td>
                      </tr>
                    )}
                    {datos.map((fila, index) => (
                      <tr key={`${fila.id}-${fila.recibo_id || "sin-recibo"}-${index}`}>
                        <td className="reporte-actions">
                          <button type="button" className="btn btn-outline-primary btn-sm me-1" onClick={() => abrirObservacion(fila)}>
                            <FontAwesomeIcon icon={faPen} /> Nota
                          </button>
                          {fila.recibo_id && (
                            <button type="button" className="btn btn-outline-success btn-sm" onClick={() => abrirEstado(fila)}>
                              Estado
                            </button>
                          )}
                        </td>
                        <td>{fila.orden_interna}</td>
                        <td>{fila.archivo}</td>
                        <td>{fila.orden_externa}</td>
                        <td>{formatoFecha(fila.fecha_inicial)}</td>
                        <td>{fila.profesional}</td>
                        <td>{fila.cedula}</td>
                        <td>{fila.paciente}</td>
{/*                         <td>{fila.factura}</td>
                        <td>{fila.porcentaje}</td> */}
                        <td>{formatoMoneda(fila.valor_copago)}</td>
                        <td>{formatoMoneda(fila.valor_recibido)}</td>
                        <td><span className={claseEstado(fila.estado_cuenta_nombre)}>{fila.estado_cuenta_nombre || "PENDIENTE"}</span></td>
                        <td>{formatoFecha(fila.fecha_recibo)}</td>
                        <td className="reporte-comprobante">
                          {fila.comprobante ? (
                            <a
                              className="btn btn-outline-info btn-sm"
                              href={`${apiImage}${fila.comprobante}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Ver comprobante"
                            >
                              <FontAwesomeIcon icon={faEye} /> Ver
                            </a>
                          ) : (
                            <span className="text-muted">Sin soporte</span>
                          )}
                        </td>
                        <td className="notas-cell">{fila.obs_interna || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalObservacion.abierto && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Notas internas orden #{modalObservacion.fila?.id}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalObservacion({ abierto: false, fila: null, valor: "" })}></button>
                </div>
                <div className="modal-body">
                  <textarea
                    className="form-control"
                    rows="5"
                    value={modalObservacion.valor}
                    onChange={(event) => setModalObservacion((actual) => ({ ...actual, valor: event.target.value }))}
                    placeholder="Escriba las observaciones internas"
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModalObservacion({ abierto: false, fila: null, valor: "" })}>Cancelar</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={guardarObservacion}>
                    <FontAwesomeIcon icon={faSave} /> Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {modalEstado.abierto && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Cambiar estado recibo #{modalEstado.fila?.recibo_id}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalEstado({ abierto: false, fila: null, estado: "" })}></button>
                </div>
                <div className="modal-body">
                  <label htmlFor="modal_estado_cuenta">Estado cuenta</label>
                  <select
                    id="modal_estado_cuenta"
                    className="form-select"
                    value={modalEstado.estado}
                    onChange={(event) => setModalEstado((actual) => ({ ...actual, estado: event.target.value }))}
                  >
                    <option value="">Seleccione</option>
                    {estadosCuenta.filter((estado) => estado.value !== "" && estado.value !== "pendiente").map((estado) => (
                      <option key={estado.value} value={estado.value}>{estado.label}</option>
                    ))}
                  </select>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModalEstado({ abierto: false, fila: null, estado: "" })}>Cancelar</button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={guardarEstado}>
                    <FontAwesomeIcon icon={faSave} /> Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default Reporte;
